import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";

import { TRADE_CATS } from "@/lib/trades";

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name:"", email:"", password:"", phone:"", role:"customer", trade_category:"" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  usePageTitle("Join Olivia — Olivia Does");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    if (payload.role !== "trade") delete payload.trade_category;
    const res = await register(payload);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome to Olivia.");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md od-card p-8">
        <h1 className="font-display text-3xl">Join Olivia</h1>
        <p className="text-sm text-[#78716C] mt-2">Book trusted home services or apply to our partner network.</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="role-customer" onClick={() => setForm({...form, role:"customer"})} className={`rounded-xl p-3 text-sm border ${form.role==="customer" ? "border-[#2A2624] bg-[#F4EFEA]" : "border-[#E2DBD6] bg-white"}`}>I'm a customer</button>
            <button type="button" data-testid="role-trade" onClick={() => setForm({...form, role:"trade"})} className={`rounded-xl p-3 text-sm border ${form.role==="trade" ? "border-[#2A2624] bg-[#F4EFEA]" : "border-[#E2DBD6] bg-white"}`}>I'm a tradesperson</button>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#78716C]">Full name</Label>
            <Input data-testid="reg-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#78716C]">Email</Label>
            <Input data-testid="reg-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#78716C]">Phone (optional)</Label>
            <Input data-testid="reg-phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" />
          </div>
          {form.role === "trade" && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#78716C]">Trade category</Label>
              <Select value={form.trade_category} onValueChange={(v)=>setForm({...form, trade_category:v})}>
                <SelectTrigger data-testid="reg-trade-cat" className="mt-2 rounded-xl border-[#E2DBD6]"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {TRADE_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#78716C] mt-2" data-testid="reg-trade-note">After signing up, you'll complete a quick onboarding checklist (insurance, ID & certifications) before accepting jobs.</p>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#78716C]">Password</Label>
            <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className="mt-2 rounded-xl border-[#E2DBD6]" />
          </div>
          <Button data-testid="register-submit" disabled={busy} type="submit" className="w-full rounded-full bg-[#2A2624] hover:bg-[#1E1A18] py-6">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        <p className="text-sm text-[#78716C] mt-6">Already with us? <Link to="/login" className="od-link font-medium">Sign in</Link></p>
      </div>
    </div>
  );
}
